import type React from "react";
import { type ReactNode } from "react";
import {
  memo,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
} from "react";

import { Transition } from "@headlessui/react";

import { useLiveRegion } from "@react/a11y/liveRegion";
import { useModalAria, useModalBackdropAria } from "@react/a11y/modalAria";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { useFocusManager } from "@tablescape/react/hooks/useFocusManager";
import { type PageModal, type TablescapeState } from "@tablescape/state";
import { tw } from "classnames";

const transitionAnimation = {
  backdrop: {
    enter: tw("transition-opacity", "ease-linear", "duration-300"),
    enterFrom: tw("opacity-0"),
    enterTo: tw("opacity-80"),
    leave: tw("transition-opacity", "ease-linear", "duration-300"),
    leaveFrom: tw("opacity-80"),
    leaveTo: tw("opacity-0"),
  },
  modal: {
    enter: tw("transition", "ease-in-out", "duration-300"),
    enterFrom: tw("opacity-0"),
    enterTo: tw("opacity-100"),
    leave: tw("transition", "ease-in-out", "duration-300"),
    leaveFrom: tw("opacity-100"),
    leaveTo: tw("opacity-0"),
  },
};

const styles = {
  container: tw(
    "fixed",
    "left-0",
    "top-0",
    "z-max",
    "w-full",
    "flex",
    "flex-row",
    "flex-nowrap",
    "justify-center",
    "items-center"
  ),
  backdrop: tw("h-screen", "w-full", "bg-overlay", "opacity-80"),
  modal: tw("fixed"),
};

type CloseModal = (
  event: React.MouseEvent<HTMLElement> | KeyboardEvent
) => void;

export type ModalContainerProps = {
  readonly "aria-label"?: string;
  readonly "aria-labelledby"?: string;
  readonly "aria-modal": true;
  readonly ref: (node: HTMLDivElement | null) => void;
  readonly role: "dialog";
  readonly tabIndex: -1;
};

type RenderProps = {
  closeModal: CloseModal;
  modalProps: ModalContainerProps;
};

const ModalProviderBase = ({
  render,
  targetModal,
}: {
  render: (props: RenderProps) => ReactNode;
  targetModal: PageModal;
}) => {
  const {
    ui: { pageModalTarget },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );

  const pageModalOpen = useMemo(
    () => pageModalTarget === targetModal,
    [pageModalTarget, targetModal]
  );

  const closeModal = useCallback<CloseModal>((e) => {
    e.preventDefault();
    TablescapeContext.emit(TablescapeEvent.TOGGLE_PAGE_MODAL, {
      ui: {
        pageModalTarget: null,
      },
    });
  }, []);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { announceModalState } = useLiveRegion();

  const { activate, deactivate, setContainer } = useFocusManager({
    active: pageModalOpen,
    returnFocusOnDeactivate: true,
    onActivate: () => {
      announceModalState("opened", String(targetModal));
    },
    onDeactivate: () => {
      announceModalState("closed", String(targetModal));
    },
  });

  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      setContainer(node);
    },
    [setContainer]
  );

  const modalAria = useModalAria({
    title: "Modal",
  });

  const backdropAria = useModalBackdropAria();

  const memoizedModal = useMemo(
    () =>
      render({
        closeModal,
        modalProps: {
          "aria-modal": true,
          role: "dialog",
          tabIndex: -1,
          ref: combinedRef,
          "aria-label": modalAria["aria-label"],
          "aria-labelledby": modalAria["aria-labelledby"],
        },
      }),
    [closeModal, combinedRef, render, modalAria]
  );

  useEffect(() => {
    if (pageModalOpen) {
      const scrollY = window.scrollY;
      document.body.classList.add("overflow-hidden");
      document.body.classList.add("fixed");
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.classList.remove("overflow-hidden");
      document.body.classList.remove("fixed");
      if (scrollY) window.scrollTo(0, parseInt(scrollY) * -1);
    }
  }, [pageModalOpen]);

  useEffect(() => {
    if (!pageModalOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" && event.key !== "Esc") {
        return;
      }

      const container = containerRef.current;

      if (container && !container.contains(event.target as Node)) {
        return;
      }

      closeModal(event);
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeModal, pageModalOpen]);

  const handleAfterEnter = useCallback(() => {
    requestAnimationFrame(() => {
      activate();
    });
  }, [activate]);

  const handleAfterLeave = useCallback(() => {
    deactivate();
  }, [deactivate]);

  return (
    <Transition className={styles.container} show={pageModalOpen}>
      {/* Backdrop */}
      <Transition.Child
        className={styles.backdrop}
        onClick={closeModal}
        {...transitionAnimation.backdrop}
        {...backdropAria}
      />
      {/* Modal */}
      <Transition.Child
        afterEnter={handleAfterEnter}
        afterLeave={handleAfterLeave}
        className={styles.modal}
        {...transitionAnimation.modal}
      >
        {memoizedModal}
      </Transition.Child>
    </Transition>
  );
};

export const ModalProvider = memo(ModalProviderBase);
